<template>
    <div class="creative-moderation">
        <header class="creative-moderation__header">
            <h1 class="creative-moderation__title">Модерация RTB-креативов</h1>
        </header>

        <section v-if="creatives.length === 0" class="creative-moderation__empty">
            <p>Нет RTB-креативов на модерации</p>
        </section>

        <section v-else class="creative-moderation__list">
            <article
                v-for="creative in creatives"
                :key="creative.id"
                class="moderation-card moderation-card--rtb"
                :class="{ 'moderation-card--rtb-expanded': isCreativeExpanded(creative.id) }"
            >
                <div class="moderation-card__summary">
                    <button
                        type="button"
                        class="moderation-card__expand"
                        :aria-expanded="isCreativeExpanded(creative.id)"
                        :aria-label="isCreativeExpanded(creative.id) ? 'Свернуть' : 'Развернуть'"
                        @click="toggleCreativeExpanded(creative.id)"
                    >
                        <span
                            class="moderation-card__chevron"
                            :class="{ 'moderation-card__chevron--open': isCreativeExpanded(creative.id) }"
                            aria-hidden="true"
                        ></span>
                    </button>

                    <div class="moderation-card__summary-main">
                        <div class="moderation-card__titles">
                            <div class="moderation-card__title-row">
                                <h3 class="moderation-card__title" :title="creative.name">{{ creative.name }}</h3>
                                <div class="moderation-card__info-wrap" :data-info-creative="creative.id">
                                    <button
                                        type="button"
                                        class="moderation-card__info-btn"
                                        :aria-expanded="infoCreativeId === creative.id"
                                        @click.stop="toggleInfoPopover(creative.id)"
                                    >
                                        Инфо
                                    </button>
                                    <div
                                        v-if="infoCreativeId === creative.id"
                                        class="moderation-card__info-popover"
                                        role="dialog"
                                        aria-label="Информация о креативе"
                                        @click.stop
                                    >
                                        <div class="moderation-card__info-row">
                                            <span class="moderation-card__info-label">ID</span>
                                            <span class="moderation-card__info-value">{{ creative.id }}</span>
                                        </div>
                                        <div class="moderation-card__info-row">
                                            <span class="moderation-card__info-label">Загружено</span>
                                            <span class="moderation-card__info-value">
                                                {{ formatDate(creative.created_at) }}
                                                <template v-if="creative.created_by?.email">
                                                    · {{ creative.created_by.email }}
                                                </template>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="moderation-card__meta-row">
                                <span v-if="creativeSize(creative)" class="meta-size">{{ creativeSize(creative) }}</span>
                                <span v-if="creativeDurationMs(creative)" class="meta-duration">
                                    {{ creativeDurationMs(creative) }} мс
                                </span>
                            </div>
                        </div>
                        <div class="moderation-card__chips">
                            <span v-if="creative.brand" class="chip">{{ brandLabel(creative) }}</span>
                            <span
                                class="chip moderation-card__targets-chip"
                                :class="{ 'chip--required': hasRequiredTargets(creative) }"
                            >{{ getTargetsSummaryLabel(creative) }}</span>
                            <span
                                v-if="readinessCoverageLabel(creative)"
                                class="chip chip--coverage"
                                :title="readinessTooltip(creative)"
                            >{{ readinessCoverageLabel(creative) }}</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        class="moderation-card__status-hit"
                        :aria-expanded="isCreativeExpanded(creative.id)"
                        @click="toggleCreativeExpanded(creative.id)"
                    >
                        <span
                            class="status-pill moderation-card__summary-status"
                            :class="publicationStatusClass(creative.status)"
                            :title="readinessTooltip(creative)"
                        >
                            {{ workflowStatusLabel(creative) }}
                        </span>
                    </button>
                </div>

                <div v-show="isCreativeExpanded(creative.id)" class="moderation-card__details moderation-card--grid">
                    <div
                        v-if="getCreativeVariants(creative).length"
                        class="moderation-card__previews"
                        :data-creative-previews="creative.id"
                    >
                        <div
                            v-for="variant in getCreativeVariants(creative)"
                            :key="variant.locale"
                            class="moderation-card__preview"
                        >
                            <span class="preview-locale">{{ variant.locale.toUpperCase() }}</span>
                            <img v-if="creative.type === 'picture'" :src="variant.url" :alt="creative.name" class="preview-thumb" />
                            <video
                                v-else
                                :src="variant.url"
                                class="preview-thumb"
                                controls
                                playsinline
                                preload="metadata"
                            ></video>
                        </div>
                    </div>

                    <div class="moderation-card__main">
                        <div class="targets-table-wrap">
                            <table class="targets-table">
                                <thead>
                                    <tr>
                                        <th>SSP</th>
                                        <th>Организация</th>
                                        <th>Язык</th>
                                        <th>Статус</th>
                                        <th v-if="hasCityscreenSendField(creative)">Отправка</th>
                                        <th>Действие</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="platform in getPlatformRows(creative)" :key="platform.row_key">
                                        <!-- CityScreen: сворачиваемая группа организаций -->
                                        <template v-if="platform.ssp === 'cityscreen'">
                                            <tr
                                                class="targets-table__platform-row"
                                                :class="{
                                                    'targets-table__row--required': platform.required,
                                                    'targets-table__row--optional': !platform.required,
                                                }"
                                            >
                                                <td>
                                                    <button
                                                        type="button"
                                                        class="targets-table__platform-toggle"
                                                        :aria-expanded="isCityscreenExpanded(creative.id)"
                                                        @click="toggleCityscreenExpanded(creative.id)"
                                                    >
                                                        <span
                                                            class="moderation-card__chevron targets-table__platform-chevron"
                                                            :class="{
                                                                'moderation-card__chevron--open': isCityscreenExpanded(creative.id),
                                                            }"
                                                            aria-hidden="true"
                                                        ></span>
                                                        <span :class="['ssp-badge', sspBadgeClass(platform.ssp)]">
                                                            {{ platform.platform_label }}
                                                        </span>
                                                    </button>
                                                </td>
                                                <td colspan="1">
                                    <span class="targets-table__orgs-summary">
                                        {{ platform.orgs.length }} орг.
                                        <span
                                            v-if="platform.required"
                                            class="target-required-badge"
                                        >В кампаниях</span>
                                    </span>
                                                </td>
                                                <td colspan="1"><span class="locale-block--empty">—</span></td>
                                                <td colspan="1"><span class="locale-block--empty">—</span></td>
                                                <td v-if="hasCityscreenSendField(creative)">
                                                    <span class="locale-block--empty">—</span>
                                                </td>
                                                <td><span class="locale-block--empty">—</span></td>
                                            </tr>

                                            <template v-if="isCityscreenExpanded(creative.id)">
                                                <template
                                                    v-for="group in platform.orgs"
                                                    :key="`${platform.row_key}-${group.group_key}`"
                                                >
                                                    <tr
                                                        v-for="(locale, localeIndex) in localeOrder"
                                                        :key="`${group.group_key}-${locale}`"
                                                        class="targets-table__org-row"
                                                        :class="{
                                                            'targets-table__row--required': group.required,
                                                            'targets-table__row--optional': !group.required,
                                                        }"
                                                    >
                                                        <td
                                                            v-if="localeIndex === 0"
                                                            :rowspan="localeOrder.length"
                                                            class="targets-table__rowspan targets-table__ssp-indent"
                                                        ></td>
                                                        <td
                                                            v-if="localeIndex === 0"
                                                            :rowspan="localeOrder.length"
                                                            class="targets-table__rowspan"
                                                        >
                                                            <div class="target-label">
                                                                <div class="target-label__name">
                                                                    {{ getGroupTargetName(group) }}
                                                                    <span
                                                                        v-if="group.required"
                                                                        class="target-required-badge"
                                                                    >В кампаниях</span>
                                                                </div>
                                                                <div v-if="group.oid" class="target-label__oid">{{ group.oid }}</div>
                                                                <div
                                                                    v-if="isAdmin && group.oid"
                                                                    class="target-label__ssp-links"
                                                                >
                                                                    <a
                                                                        :href="sspScreensHref('cityscreen', group.oid)"
                                                                        class="target-label__ssp-icon"
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title="Экраны"
                                                                        aria-label="Экраны"
                                                                    >
                                                                        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                                                                            <rect x="2" y="3" width="20" height="14" rx="2" />
                                                                            <path d="M8 21h8M12 17v4" />
                                                                        </svg>
                                                                    </a>
                                                                    <a
                                                                        :href="sspCreativesHref('cityscreen', group)"
                                                                        class="target-label__ssp-icon"
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title="Креативы"
                                                                        aria-label="Креативы"
                                                                    >
                                                                        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                                                                            <rect x="3" y="3" width="18" height="18" rx="2" />
                                                                            <circle cx="9" cy="9" r="2" />
                                                                            <path d="m21 15-3.5-3.5a2 2 0 0 0-2.8 0L6 20" />
                                                                        </svg>
                                                                    </a>
                                                                </div>
                                                                <div
                                                                    v-if="group.required && group.bindings"
                                                                    class="target-label__bindings"
                                                                    :title="bindingsTitle(group)"
                                                                >
                                                                    {{ bindingsSummary(group) }}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span class="locale-pill">{{ locale.toUpperCase() }}</span>
                                                        </td>
                                                        <td>
                                                            <template v-if="group.locales[locale]">
                                                                <div class="status-cell">
                                                                    <div class="status-cell__row">
                                                                        <span
                                                                            class="status-pill"
                                                                            :class="publicationStatusClass(group.locales[locale].publication?.status)"
                                                                        >
                                                                            {{ statusLabel(group.locales[locale].publication?.status) }}
                                                                        </span>
                                                                        <button
                                                                            v-if="isAdmin && canRefreshPublicationStatus(group.locales[locale])"
                                                                            type="button"
                                                                            class="update-btn"
                                                                            :class="{
                                                                                'update-btn--spinning': isPublicationRefreshBusy(
                                                                                    creative.id,
                                                                                    group.locales[locale].target_key
                                                                                ),
                                                                                'update-btn--disabled': isPublicationRefreshBusy(
                                                                                    creative.id,
                                                                                    group.locales[locale].target_key
                                                                                ),
                                                                            }"
                                                                            :disabled="isPublicationRefreshBusy(creative.id, group.locales[locale].target_key)"
                                                                            aria-label="Обновить статус из SSP"
                                                                            @click="refreshPublicationFromSsp(creative, group.locales[locale])"
                                                                        ></button>
                                                                    </div>
                                                                    <div
                                                                        v-if="publicationCommentPreview(group.locales[locale].publication?.comment)"
                                                                        class="publication-comment"
                                                                        :title="publicationCommentPreview(group.locales[locale].publication?.comment)"
                                                                    >
                                                                        {{ publicationCommentPreview(group.locales[locale].publication?.comment) }}
                                                                    </div>
                                                                </div>
                                                            </template>
                                                            <span v-else class="locale-block--empty">—</span>
                                                        </td>
                                                        <td v-if="hasCityscreenSendField(creative)">
                                                            <template v-if="group.locales[locale]">
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    class="target-api-input"
                                                                    :value="targetApiIds[getTargetStateKey(creative.id, group.locales[locale].target_key)] ?? ''"
                                                                    @input="setTargetApiId(creative.id, group.locales[locale].target_key, $event.target.value)"
                                                                    :disabled="isTargetSending(creative.id, group.locales[locale].target_key) || isTargetLocked(group.locales[locale])"
                                                                    placeholder="id"
                                                                />
                                                            </template>
                                                            <span v-else class="locale-block--empty">—</span>
                                                        </td>
                                                        <td>
                                                            <template v-if="group.locales[locale]">
                                                                <button
                                                                    class="act act--approve"
                                                                    :disabled="!canSendTarget(creative, group.locales[locale])"
                                                                    @click="sendTarget(creative, group.locales[locale])"
                                                                >
                                                                    {{ isTargetSending(creative.id, group.locales[locale].target_key) ? '...' : 'Отправить' }}
                                                                </button>
                                                            </template>
                                                            <span v-else class="locale-block--empty">—</span>
                                                        </td>
                                                    </tr>
                                                </template>
                                            </template>
                                        </template>

                                        <!-- Остальные SSP — плоские строки -->
                                        <template v-else>
                                            <tr
                                                v-for="(locale, localeIndex) in localeOrder"
                                                :key="`${platform.row_key}-${locale}`"
                                                :class="{
                                                    'targets-table__row--required': platform.group.required,
                                                    'targets-table__row--optional': !platform.group.required,
                                                }"
                                            >
                                                <td
                                                    v-if="localeIndex === 0"
                                                    :rowspan="localeOrder.length"
                                                    class="targets-table__rowspan"
                                                >
                                                    <span :class="['ssp-badge', sspBadgeClass(platform.ssp)]">
                                                        {{ platform.platform_label }}
                                                    </span>
                                                </td>
                                                <td
                                                    v-if="localeIndex === 0"
                                                    :rowspan="localeOrder.length"
                                                    class="targets-table__rowspan"
                                                >
                                                    <div class="target-label">
                                                        <div class="target-label__name">
                                                            {{ getGroupTargetName(platform.group) }}
                                                            <span
                                                                v-if="platform.group.required"
                                                                class="target-required-badge"
                                                            >В кампаниях</span>
                                                        </div>
                                                        <div
                                                            v-if="isAdmin && platform.ssp === 'citix'"
                                                            class="citix-frames"
                                                            :data-citix-frames="creative.id"
                                                        >
                                                            <button
                                                                type="button"
                                                                class="citix-frames__trigger"
                                                                :aria-expanded="citixPickerCreativeId === creative.id ? 'true' : 'false'"
                                                                @click.stop="toggleCitixPicker(creative.id)"
                                                            >
                                                                {{ citixSummary(creative.id) }}
                                                            </button>
                                                            <div
                                                                v-if="citixPickerCreativeId === creative.id"
                                                                class="citix-frames__panel"
                                                                role="listbox"
                                                                aria-multiselectable="true"
                                                                @click.stop
                                                            >
                                                                <p v-if="!citixState(creative.id).loaded" class="citix-frames__hint">Загрузка…</p>
                                                                <p v-else-if="citixState(creative.id).options.length === 0" class="citix-frames__hint">Нет экранов Citix</p>
                                                                <label
                                                                    v-for="option in citixState(creative.id).options"
                                                                    :key="option.gid"
                                                                    class="citix-frames__option"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        class="custom-checkbox"
                                                                        :checked="isCitixGidChecked(creative.id, option.gid)"
                                                                        @change="toggleCitixGid(creative.id, option.gid)"
                                                                    />
                                                                    <span class="citix-frames__option-label">
                                                                        <span class="citix-frames__gid">{{ option.gid }}</span>
                                                                        <span v-if="option.address" class="citix-frames__address">{{ option.address }}</span>
                                                                    </span>
                                                                </label>
                                                            </div>
                                                            <div class="citix-frames__actions">
                                                                <button
                                                                    type="button"
                                                                    class="act"
                                                                    :disabled="!canSaveCitixFrames(creative.id)"
                                                                    @click="saveCitixFrames(creative)"
                                                                >
                                                                    {{ citixState(creative.id).saving ? '...' : 'Сохранить' }}
                                                                </button>
                                                                <span v-if="citixHint(creative.id)" class="citix-frames__hint">{{ citixHint(creative.id) }}</span>
                                                            </div>
                                                        </div>
                                                        <div
                                                            v-if="isAdmin"
                                                            class="target-label__ssp-links"
                                                        >
                                                            <a
                                                                v-if="sspScreensHref(platform.ssp)"
                                                                :href="sspScreensHref(platform.ssp)"
                                                                class="target-label__ssp-icon"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title="Экраны"
                                                                aria-label="Экраны"
                                                            >
                                                                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                                                                    <rect x="2" y="3" width="20" height="14" rx="2" />
                                                                    <path d="M8 21h8M12 17v4" />
                                                                </svg>
                                                            </a>
                                                            <a
                                                                v-if="sspCreativesHref(platform.ssp, platform.group)"
                                                                :href="sspCreativesHref(platform.ssp, platform.group)"
                                                                class="target-label__ssp-icon"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title="Креативы"
                                                                aria-label="Креативы"
                                                            >
                                                                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                                                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                                                    <circle cx="9" cy="9" r="2" />
                                                                    <path d="m21 15-3.5-3.5a2 2 0 0 0-2.8 0L6 20" />
                                                                </svg>
                                                            </a>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span class="locale-pill">{{ locale.toUpperCase() }}</span>
                                                </td>
                                                <td>
                                                    <template v-if="platform.group.locales[locale]">
                                                        <div class="status-cell">
                                                            <div class="status-cell__row">
                                                                <span
                                                                    class="status-pill"
                                                                    :class="publicationStatusClass(platform.group.locales[locale].publication?.status)"
                                                                >
                                                                    {{ statusLabel(platform.group.locales[locale].publication?.status) }}
                                                                </span>
                                                                <button
                                                                    v-if="isAdmin && canRefreshPublicationStatus(platform.group.locales[locale])"
                                                                    type="button"
                                                                    class="update-btn"
                                                                    :class="{
                                                                        'update-btn--spinning': isPublicationRefreshBusy(
                                                                            creative.id,
                                                                            platform.group.locales[locale].target_key
                                                                        ),
                                                                        'update-btn--disabled': isPublicationRefreshBusy(
                                                                            creative.id,
                                                                            platform.group.locales[locale].target_key
                                                                        ),
                                                                    }"
                                                                    :disabled="isPublicationRefreshBusy(creative.id, platform.group.locales[locale].target_key)"
                                                                    aria-label="Обновить статус из SSP"
                                                                    @click="refreshPublicationFromSsp(creative, platform.group.locales[locale])"
                                                                ></button>
                                                            </div>
                                                            <div
                                                                v-if="publicationCommentPreview(platform.group.locales[locale].publication?.comment)"
                                                                class="publication-comment"
                                                                :title="publicationCommentPreview(platform.group.locales[locale].publication?.comment)"
                                                            >
                                                                {{ publicationCommentPreview(platform.group.locales[locale].publication?.comment) }}
                                                            </div>
                                                        </div>
                                                    </template>
                                                    <span v-else class="locale-block--empty">—</span>
                                                </td>
                                                <td v-if="hasCityscreenSendField(creative)">
                                                    <span class="locale-block--empty">—</span>
                                                </td>
                                                <td>
                                                    <template v-if="platform.group.locales[locale]">
                                                        <button
                                                            class="act act--approve"
                                                            :disabled="!canSendTarget(creative, platform.group.locales[locale])"
                                                            @click="sendTarget(creative, platform.group.locales[locale])"
                                                        >
                                                            {{ isTargetSending(creative.id, platform.group.locales[locale].target_key) ? '...' : 'Отправить' }}
                                                        </button>
                                                    </template>
                                                    <span v-else class="locale-block--empty">—</span>
                                                </td>
                                            </tr>
                                        </template>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                        <p v-if="targetErrors[creative.id]" class="target-error">{{ targetErrors[creative.id] }}</p>
                    </div>
                </div>
            </article>
        </section>
    </div>
</template>

<script>
import axios from 'axios';
import { formatInRtbTZ } from '../../../utils/timezones';
import {
    getReadinessCoverageLabel,
    buildMissingTargetsTooltip,
    getAdminCreativeStatusWithCoverage,
} from '../../../utils/creativeStatus';

export default {
    name: 'RtbCreativeModeration',
    props: {
        user: { type: Object, required: true },
        initialCreatives: { type: Array, default: () => [] },
        initialNextCityscreenApiId: { type: Number, default: 1 },
    },
    data() {
        return {
            creatives: [],
            targetApiIds: {},
            targetApiIdTouched: {},
            nextCityscreenApiId: 1,
            processingTargetKey: null,
            processingPublicationRefreshKey: null,
            targetErrors: {},
            localeOrder: ['ru', 'kz'],
            expandedCreativeIds: {},
            expandedCityscreenIds: {},
            infoCreativeId: null,
            citixFrames: {},
            citixPickerCreativeId: null,
            _onDocClick: null,
            _onDocKeydown: null,
        };
    },
    computed: {
        isAdmin() {
            return this.user?.is_admin === true;
        },
    },
    mounted() {
        const next = Number(this.initialNextCityscreenApiId);
        this.nextCityscreenApiId = Number.isInteger(next) && next > 0 ? next : 1;
        this.creatives = (Array.isArray(this.initialCreatives) ? this.initialCreatives : []).map(c => this.normalizeCreative(c));
        this.prefillEmptyCityscreenApiIds();

        this._onDocClick = (event) => {
            if (this.infoCreativeId != null) {
                const wrap = event.target?.closest?.(`[data-info-creative="${this.infoCreativeId}"]`);
                if (!wrap) this.infoCreativeId = null;
            }
            if (this.citixPickerCreativeId != null) {
                const picker = event.target?.closest?.(`[data-citix-frames="${this.citixPickerCreativeId}"]`);
                if (!picker) this.citixPickerCreativeId = null;
            }
        };
        this._onDocKeydown = (event) => {
            if (event.key === 'Escape' && this.infoCreativeId != null) {
                this.infoCreativeId = null;
            }
        };
        document.addEventListener('click', this._onDocClick);
        document.addEventListener('keydown', this._onDocKeydown);
    },
    beforeUnmount() {
        document.removeEventListener('click', this._onDocClick);
        document.removeEventListener('keydown', this._onDocKeydown);
    },
    methods: {
        isCreativeExpanded(creativeId) {
            return Boolean(this.expandedCreativeIds[creativeId]);
        },
        toggleCreativeExpanded(creativeId) {
            this.expandedCreativeIds = {
                ...this.expandedCreativeIds,
                [creativeId]: !this.expandedCreativeIds[creativeId],
            };
            if (this.infoCreativeId === creativeId) {
                this.infoCreativeId = null;
            }
            if (this.expandedCreativeIds[creativeId] && this.isAdmin) {
                this.loadCitixFrames(creativeId);
            }
            if (!this.expandedCreativeIds[creativeId]) {
                this.pauseCreativePreviews(creativeId);
            }
        },
        pauseCreativePreviews(creativeId) {
            const root = this.$el?.querySelector?.(`[data-creative-previews="${creativeId}"]`);
            root?.querySelectorAll('video').forEach((video) => {
                video.pause();
            });
        },
        isCityscreenExpanded(creativeId) {
            return Boolean(this.expandedCityscreenIds[creativeId]);
        },
        toggleCityscreenExpanded(creativeId) {
            this.expandedCityscreenIds = {
                ...this.expandedCityscreenIds,
                [creativeId]: !this.expandedCityscreenIds[creativeId],
            };
        },
        toggleInfoPopover(creativeId) {
            this.infoCreativeId = this.infoCreativeId === creativeId ? null : creativeId;
        },
        creativeSize(creative) {
            return creative?.size || (creative?.width && creative?.height ? `${creative.width}x${creative.height}` : '');
        },
        creativeDurationMs(creative) {
            const fromVariant = creative?.variants?.find(v => v.locale === 'ru')?.duration
                ?? creative?.variants?.[0]?.duration;
            const value = Number(fromVariant ?? creative?.duration ?? 0);
            return Number.isFinite(value) && value > 0 ? Math.floor(value) : null;
        },
        sspBadgeClass(ssp) {
            const key = String(ssp || '').toLowerCase();
            if (key === 'nowmedia') return 'ssp-badge--spinetix';
            return `ssp-badge--${key || 'unknown'}`;
        },
        getPlatformRows(creative) {
            const groups = this.getTargetGroups(creative);
            const cityscreen = groups.filter(group => group.ssp === 'cityscreen');
            const others = groups.filter(group => group.ssp !== 'cityscreen');
            const rows = [];

            if (cityscreen.length > 0) {
                rows.push({
                    row_key: 'cityscreen',
                    ssp: 'cityscreen',
                    platform_label: 'CityScreen',
                    required: cityscreen.some(group => group.required),
                    orgs: cityscreen,
                    group: null,
                });
            }

            for (const group of others) {
                rows.push({
                    row_key: group.group_key,
                    ssp: group.ssp,
                    platform_label: group.platform_label || this.getPlatformLabel(group.ssp),
                    required: Boolean(group.required),
                    orgs: null,
                    group,
                });
            }

            return rows;
        },
        getTargetsSummaryLabel(creative) {
            const groups = this.getTargetGroups(creative);
            const required = groups.filter(group => group.required);
            if (required.length > 0) {
                const parts = [];
                const cityscreen = required.filter(group => group.ssp === 'cityscreen');
                if (cityscreen.length > 0) {
                    const names = cityscreen
                        .map(group => group.organization_name || group.oid)
                        .filter(Boolean);
                    parts.push(names.length ? `CityScreen (${names.join(', ')})` : 'CityScreen');
                }
                if (required.some(group => group.ssp === 'spinetix')) {
                    parts.push('Spinetix');
                }
                if (required.some(group => group.ssp === 'ledex')) {
                    parts.push('LEDEX');
                }
                if (required.some(group => group.ssp === 'citix')) {
                    parts.push('Citix');
                }
                return `Нужно: ${parts.join(', ')}`;
            }

            const sent = (creative.moderation_targets || []).filter(target => {
                const status = String(target?.publication?.status || '').toLowerCase();
                const hasApiId = target?.publication?.api_id != null && String(target.publication.api_id).trim() !== '';
                return status !== 'pending_approval' || hasApiId;
            }).length;
            const total = creative.moderation_targets?.length || 0;
            return `${groups.length} SSP · ${sent}/${total} отправлено`;
        },
        hasRequiredTargets(creative) {
            return this.getTargetGroups(creative).some(group => group.required);
        },
        normalizeCreative(creative) {
            const normalized = { ...creative };
            const targets = Array.isArray(normalized.moderation_targets) ? normalized.moderation_targets : [];
            normalized.moderation_targets = targets.map(target => ({
                target_key: target.target_key || `${target.ssp}:${target.oid || 'global'}:${target.locale || 'ru'}`,
                ssp: target.ssp,
                locale: target.locale || 'ru',
                platform_label: target.platform_label || this.getPlatformLabel(target.ssp),
                oid: target.oid || null,
                organization_name: target.organization_name || null,
                variant_url: target.variant_url || null,
                required: Boolean(target.required),
                bindings: target.bindings || null,
                publication: target.publication || null,
            }));
            normalized.variants = Array.isArray(normalized.variants) ? normalized.variants : [];

            normalized.moderation_targets.forEach(target => {
                const key = this.getTargetStateKey(normalized.id, target.target_key);
                if (target.ssp === 'cityscreen' && this.canReuseCityscreenApiId(target) && target.publication?.api_id) {
                    this.targetApiIds[key] = target.publication.api_id;
                }
            });

            return normalized;
        },
        getTargetStateKey(creativeId, targetKey) {
            return `${creativeId}:${targetKey}`;
        },
        getTargetGroups(creative) {
            const groups = new Map();
            for (const target of creative.moderation_targets || []) {
                const groupKey = ['spinetix', 'ledex', 'citix'].includes(target.ssp) ? target.ssp : `${target.ssp}:${target.oid}`;
                if (!groups.has(groupKey)) {
                    groups.set(groupKey, {
                        group_key: groupKey,
                        ssp: target.ssp,
                        oid: target.oid,
                        platform_label: target.platform_label,
                        organization_name: target.organization_name,
                        required: Boolean(target.required),
                        bindings: target.bindings || null,
                        locales: {},
                    });
                }
                groups.get(groupKey).locales[target.locale || 'ru'] = target;
            }
            return Array.from(groups.values()).sort((a, b) => this.compareTargetGroups(a, b));
        },
        hasCityscreenSendField(creative) {
            return this.getTargetGroups(creative).some(group => group.ssp === 'cityscreen');
        },
        compareTargetGroups(a, b) {
            const requiredFirst = Number(Boolean(b.required)) - Number(Boolean(a.required));
            if (requiredFirst !== 0) {
                return requiredFirst;
            }
            const cityscreenFirst = Number(b.ssp === 'cityscreen') - Number(a.ssp === 'cityscreen');
            if (cityscreenFirst !== 0) {
                return cityscreenFirst;
            }
            if (a.ssp === 'cityscreen' && b.ssp === 'cityscreen') {
                const nameA = a.organization_name || a.oid || '';
                const nameB = b.organization_name || b.oid || '';
                return String(nameA).localeCompare(String(nameB), 'ru');
            }
            const labelA = a.platform_label || this.getPlatformLabel(a.ssp);
            const labelB = b.platform_label || this.getPlatformLabel(b.ssp);
            return String(labelA).localeCompare(String(labelB), 'ru');
        },
        bindingsSummary(group) {
            const campaigns = group?.bindings?.campaigns || [];
            const screens = Number(group?.bindings?.screen_count || 0);
            return `${campaigns.length} камп. · ${screens} экранов`;
        },
        bindingsTitle(group) {
            const names = (group?.bindings?.campaigns || [])
                .map(campaign => campaign?.name)
                .filter(Boolean);
            return names.join(', ');
        },
        getGroupTargetName(group) {
            if (group?.ssp === 'citix') return 'Citix';
            if (group?.ssp === 'ledex') return 'LEDEX';
            if (group?.ssp === 'spinetix') return 'Spinetix';
            if (!group?.oid) {
                return 'Spinetix';
            }
            return group.organization_name || 'Организация';
        },
        sspScreensHref(ssp, oid = null) {
            const key = String(ssp || '').toLowerCase();
            if (key === 'cityscreen' || key === 'cs') {
                if (!oid) return '';
                return `/api/ssp/cityscreen/inventories/${encodeURIComponent(oid)}`;
            }
            if (key === 'spinetix' || key === 'nm' || key === 'nowmedia') {
                return '/api/ssp/spinetix/inventories';
            }
            if (key === 'ledex' || key === 'lx') {
                return '/api/ssp/ledex/inventories';
            }
            if (key === 'citix' || key === 'cx') {
                return '/api/ssp/citix/inventories';
            }
            return '';
        },
        sspCreativesHref(ssp, group = null) {
            const key = String(ssp || '').toLowerCase();
            if (key === 'cityscreen' || key === 'cs') {
                const oid = group?.oid;
                if (!oid) return '';
                return `/api/ssp/cs/creatives/${encodeURIComponent(oid)}`;
            }
            if (key === 'spinetix' || key === 'nm' || key === 'nowmedia') {
                const ids = this.collectSpinetixCreativeIds(group);
                if (!ids.length) return '';
                const qs = ids.map((id) => `ids[]=${encodeURIComponent(id)}`).join('&');
                return `/api/ssp/spinetix/creatives?${qs}`;
            }
            if (key === 'ledex' || key === 'lx') {
                return '/api/ssp/ledex/creatives';
            }
            return '';
        },
        collectSpinetixCreativeIds(group) {
            const locales = group?.locales && typeof group.locales === 'object'
                ? Object.values(group.locales)
                : [];
            const ids = [];
            const seen = new Set();

            for (const target of locales) {
                const pub = target?.publication;
                if (!pub) continue;
                const candidates = [
                    pub.video_id,
                    pub.api_id,
                    pub.payload_snapshot?.video_id,
                ];
                for (const raw of candidates) {
                    const id = raw == null ? '' : String(raw).trim();
                    if (!id || seen.has(id)) continue;
                    seen.add(id);
                    ids.push(id);
                    break;
                }
            }

            return ids;
        },
        getTargetName(target) {
            if (target?.ssp === 'citix') return 'Citix';
            if (target?.ssp === 'ledex') return 'LEDEX';
            if (target?.ssp === 'spinetix') return 'Spinetix';
            if (!target?.oid) return 'Spinetix';
            return target.organization_name || 'Организация';
        },
        getPlatformLabel(ssp) {
            if (ssp === 'cityscreen') return 'CityScreen';
            if (ssp === 'spinetix' || ssp === 'nowmedia') return 'Spinetix';
            if (ssp === 'ledex') return 'LEDEX';
            if (ssp === 'citix') return 'Citix';
            return ssp;
        },
        setTargetApiId(creativeId, targetKey, value) {
            const key = this.getTargetStateKey(creativeId, targetKey);
            this.targetApiIds[key] = value;
            this.targetApiIdTouched[key] = true;
        },
        parsePositiveInt(value) {
            const n = Number(value);
            return Number.isInteger(n) && n > 0 ? n : null;
        },
        applyNextCityscreenApiId(value) {
            const next = this.parsePositiveInt(value);
            if (next != null) {
                this.nextCityscreenApiId = next;
            }
            this.prefillEmptyCityscreenApiIds();
        },
        canReuseCityscreenApiId(target) {
            if (this.isTargetLocked(target)) return true;
            return Boolean(target?.publication?.has_payload_snapshot);
        },
        prefillEmptyCityscreenApiIds() {
            const next = String(this.parsePositiveInt(this.nextCityscreenApiId) ?? 1);

            for (const creative of this.creatives) {
                for (const group of this.getTargetGroups(creative)) {
                    if (group.ssp !== 'cityscreen') continue;
                    for (const locale of this.localeOrder) {
                        const target = group.locales[locale];
                        if (!target) continue;
                        const key = this.getTargetStateKey(creative.id, target.target_key);

                        // Manual edits stay as-is when next bumps.
                        if (this.targetApiIdTouched[key]) continue;

                        // Already claimed / reusable publication keeps its api_id.
                        const publishedId = this.canReuseCityscreenApiId(target)
                            ? this.parsePositiveInt(target.publication?.api_id)
                            : null;
                        if (publishedId != null) {
                            this.targetApiIds[key] = String(publishedId);
                            continue;
                        }

                        // All remaining empty CityScreen slots share the same next id.
                        this.targetApiIds[key] = next;
                    }
                }
            }
        },
        isTargetSending(creativeId, targetKey) {
            return this.processingTargetKey === `${creativeId}:${targetKey}`;
        },
        canRefreshPublicationStatus(target) {
            const pub = target?.publication;
            if (!pub?.id || !pub?.api_id) return false;
            return Boolean(pub.status);
        },
        isPublicationRefreshBusy(creativeId, targetKey) {
            return this.processingPublicationRefreshKey === `${creativeId}:${targetKey}`;
        },
        isTargetLocked(target) {
            if (target?.ssp === 'citix') return false;
            const status = String(target?.publication?.status || '').toLowerCase();
            return [
                'sent_to_ssp',
                'confirmed',
                'approve',
                'approved',
                'review',
                'accepted',
                'waiting',
            ].includes(status);
        },
        canSendTarget(creative, target) {
            const stateKey = this.getTargetStateKey(creative.id, target.target_key);
            const raw = this.targetApiIds[stateKey];
            if (target.ssp === 'cityscreen') {
                const value = Number(raw);
                if (!Number.isInteger(value) || value <= 0) return false;
            }
            if (target.ssp === 'citix') {
                if (!this.hasSavedCitixFrames(creative.id) || this.isCitixDraftDirty(creative.id)) return false;
            }
            if (this.isTargetLocked(target)) return false;
            return !this.isTargetSending(creative.id, target.target_key);
        },
        citixState(creativeId) {
            return this.citixFrames[creativeId] || {
                options: [],
                selected: [],
                suggested: [],
                draft: [],
                loaded: false,
                loading: false,
                saving: false,
            };
        },
        ensureCitixState(creativeId) {
            if (!this.citixFrames[creativeId]) {
                this.citixFrames = {
                    ...this.citixFrames,
                    [creativeId]: {
                        options: [],
                        selected: [],
                        suggested: [],
                        draft: [],
                        loaded: false,
                        loading: false,
                        saving: false,
                    },
                };
            }
            return this.citixFrames[creativeId];
        },
        async loadCitixFrames(creativeId) {
            const state = this.ensureCitixState(creativeId);
            if (state.loaded || state.loading || state.saving) return;
            state.loading = true;
            try {
                const response = await axios.get(`/api/creatives/${creativeId}/citix-frames`);
                const selected = Array.isArray(response?.data?.selected) ? response.data.selected : [];
                const suggested = Array.isArray(response?.data?.suggested) ? response.data.suggested : [];
                state.options = Array.isArray(response?.data?.options) ? response.data.options : [];
                state.selected = selected.slice();
                state.suggested = suggested.slice();
                state.draft = (selected.length ? selected : suggested).slice();
                state.loaded = true;
            } catch (error) {
                const message = error?.response?.data?.error || 'Не удалось загрузить конструкции Citix';
                this.targetErrors = { ...this.targetErrors, [creativeId]: message };
            } finally {
                state.loading = false;
            }
        },
        toggleCitixPicker(creativeId) {
            this.citixPickerCreativeId = this.citixPickerCreativeId === creativeId ? null : creativeId;
            if (this.citixPickerCreativeId === creativeId) {
                this.loadCitixFrames(creativeId);
            }
        },
        isCitixGidChecked(creativeId, gid) {
            return this.citixState(creativeId).draft.includes(gid);
        },
        toggleCitixGid(creativeId, gid) {
            const state = this.ensureCitixState(creativeId);
            const draft = state.draft.slice();
            const index = draft.indexOf(gid);
            if (index >= 0) draft.splice(index, 1);
            else draft.push(gid);
            state.draft = draft;
        },
        sameGidSet(left, right) {
            const a = [...left].sort();
            const b = [...right].sort();
            if (a.length !== b.length) return false;
            return a.every((gid, index) => gid === b[index]);
        },
        isCitixDraftDirty(creativeId) {
            const state = this.citixState(creativeId);
            if (!state.loaded) return false;
            return !this.sameGidSet(state.draft, state.selected);
        },
        hasSavedCitixFrames(creativeId) {
            const state = this.citixState(creativeId);
            return state.loaded && state.selected.length > 0;
        },
        canSaveCitixFrames(creativeId) {
            const state = this.citixState(creativeId);
            return state.loaded && !state.saving && this.isCitixDraftDirty(creativeId);
        },
        citixSummary(creativeId) {
            const state = this.citixState(creativeId);
            const count = state.draft.length;
            if (!state.loaded) return 'Конструкции';
            if (count === 0) return 'Конструкции не выбраны';
            return `Конструкции: ${count}`;
        },
        citixHint(creativeId) {
            const state = this.citixState(creativeId);
            if (!state.loaded) return '';
            if (state.selected.length === 0 && state.suggested.length > 0 && this.sameGidSet(state.draft, state.suggested)) {
                return 'Отмечены экраны из кампаний. Сохраните список перед отправкой.';
            }
            if (this.isCitixDraftDirty(creativeId)) return 'Сохраните список перед отправкой.';
            if (state.selected.length === 0) return 'Выберите конструкции.';
            return '';
        },
        async saveCitixFrames(creative) {
            const state = this.ensureCitixState(creative.id);
            if (!this.canSaveCitixFrames(creative.id)) return;
            state.saving = true;
            this.targetErrors = { ...this.targetErrors, [creative.id]: '' };
            try {
                const response = await axios.put(`/api/creatives/${creative.id}/citix-frames`, {
                    gids: state.draft.slice(),
                });
                const selected = Array.isArray(response?.data?.selected) ? response.data.selected : state.draft.slice();
                state.selected = selected.slice();
                state.suggested = Array.isArray(response?.data?.suggested) ? response.data.suggested : state.suggested;
                state.options = Array.isArray(response?.data?.options) ? response.data.options : state.options;
                state.draft = selected.slice();
                state.loaded = true;
            } catch (error) {
                const message = error?.response?.data?.error || 'Не удалось сохранить конструкции Citix';
                this.targetErrors = { ...this.targetErrors, [creative.id]: message };
                this.$notify({
                    title: 'Ошибка',
                    message,
                    type: 'error',
                });
            } finally {
                state.saving = false;
            }
        },
        publicationCommentPreview(comment) {
            if (comment == null || typeof comment !== 'string') return '';
            const t = comment.trim();
            return t.length ? t : '';
        },
        workflowStatusLabel(creative) {
            return getAdminCreativeStatusWithCoverage(creative.status, creative.readiness_details);
        },
        readinessCoverageLabel(creative) {
            return getReadinessCoverageLabel(creative?.readiness_details);
        },
        readinessTooltip(creative) {
            return buildMissingTargetsTooltip(creative?.readiness_details);
        },
        applyCreativeAggregateFromResponse(creative, data) {
            if (!creative || !data) {
                return;
            }

            if (typeof data.creative_status === 'string' && data.creative_status !== '') {
                creative.status = data.creative_status;
            }

            if (data.readiness_details && typeof data.readiness_details === 'object') {
                creative.readiness_details = {
                    coverage: data.readiness_details.coverage || 'none',
                    missing_targets: data.readiness_details.missing_targets || [],
                    required_target_count: Number(data.readiness_details.required_target_count ?? 0),
                    ready_target_count: Number(data.readiness_details.ready_target_count ?? 0),
                };
            }
        },
        statusLabel(status) {
            if (!status) return 'Не отправлен';
            const normalized = String(status).toLowerCase();
            if (['approve', 'approved', 'confirmed'].includes(normalized)) return 'Одобрен';
            if (['rejected', 'reject'].includes(normalized)) return 'Отклонён модератором';
            if (['failed', 'fail'].includes(normalized)) return 'Ошибка проверки';
            if (['archived'].includes(normalized)) return 'В архиве';
            if (['accepted'].includes(normalized)) return 'Частично на площадках';
            if (['waiting'].includes(normalized)) return 'Ожидание площадок';
            if (['review'].includes(normalized)) return 'В очереди модератора';
            if (['sent_to_ssp'].includes(normalized)) return 'На стороне SSP';
            if (['pending_approval'].includes(normalized)) return 'Ожидает отправки';
            if (['cancelled', 'error'].includes(normalized)) return 'Отклонен';
            return status;
        },
        publicationStatusClass(status) {
            const normalized = String(status || '').toLowerCase();
            if (['approve', 'approved', 'confirmed'].includes(normalized)) return 'status-pill--ok';
            if (['rejected', 'reject', 'failed', 'fail', 'cancelled', 'error'].includes(normalized)) {
                return 'status-pill--fail';
            }
            if (['archived'].includes(normalized)) return 'status-pill--archived';
            if (!status) return 'status-pill--idle';
            return 'status-pill--pending';
        },
        async sendTarget(creative, target) {
            const stateKey = this.getTargetStateKey(creative.id, target.target_key);
            const processKey = `${creative.id}:${target.target_key}`;
            this.processingTargetKey = processKey;
            this.targetErrors[creative.id] = '';

            let payload;
            if (target.ssp === 'cityscreen') {
                const apiId = Number(this.targetApiIds[stateKey]);
                if (!Number.isInteger(apiId) || apiId <= 0) {
                    this.processingTargetKey = null;
                    return;
                }
                payload = {
                    ssp: 'cityscreen',
                    oid: target.oid,
                    api_id: apiId,
                    locale: target.locale || 'ru',
                };
            } else if (target.ssp === 'ledex') {
                payload = { ssp: 'ledex', locale: target.locale || 'ru' };
            } else if (target.ssp === 'citix') {
                payload = { ssp: 'citix', locale: target.locale || 'ru' };
            } else {
                payload = { ssp: 'spinetix', locale: target.locale || 'ru' };
            }

            try {
                const endpoint = `/api/creatives/${creative.id}/ssp/target`;
                const response = await axios.post(endpoint, payload);
                const result = response?.data?.result || null;

                if (!result?.success) {
                    const errMsg = result?.error || response?.data?.error || 'Не удалось отправить креатив';
                    this.targetErrors[creative.id] = errMsg;
                    this.$notify({
                        title: 'Ошибка',
                        message: errMsg,
                        type: 'error',
                    });
                    target.publication = {
                        ...(target.publication || {}),
                        id: result?.publication_id || target.publication?.id || null,
                        status: target.publication?.status || 'pending_approval',
                        api_id: result?.api_id != null ? String(result.api_id) : target.publication?.api_id ?? null,
                        has_payload_snapshot: result?.api_id != null
                            ? true
                            : Boolean(target.publication?.has_payload_snapshot),
                        comment: errMsg,
                        updated_at: new Date().toISOString(),
                    };
                    this.applyNextCityscreenApiId(response?.data?.next_cityscreen_api_id);
                    return;
                }

                target.publication = {
                    ...(target.publication || {}),
                    id: result?.publication_id || target.publication?.id || null,
                    status: 'sent_to_ssp',
                    api_id: result?.api_id != null ? String(result.api_id) : null,
                    has_payload_snapshot: true,
                    comment: null,
                    updated_at: new Date().toISOString(),
                    payload_snapshot: {
                        ...(target.publication?.payload_snapshot || {}),
                        ...(target.ssp === 'spinetix' && result?.video_id
                            ? { video_id: result.video_id }
                            : {}),
                    },
                };

                this.$notify({
                    title: 'Успешно',
                    message: response?.data?.message || 'Креатив отправлен',
                    type: 'success',
                });
                this.applyCreativeAggregateFromResponse(creative, response?.data);
                this.applyNextCityscreenApiId(response?.data?.next_cityscreen_api_id);
            } catch (error) {
                const payload = error?.response?.data || {};
                const message = payload.error || 'Не удалось отправить креатив';
                this.targetErrors[creative.id] = message;
                this.$notify({
                    title: 'Ошибка',
                    message,
                    type: 'error',
                });
                this.applyNextCityscreenApiId(payload.next_cityscreen_api_id);
            } finally {
                this.processingTargetKey = null;
            }
        },
        getCreativeVariants(creative) {
            if (Array.isArray(creative?.variants) && creative.variants.length > 0) {
                return creative.variants;
            }
            if (creative?.url) {
                return [{ locale: 'ru', url: creative.url }];
            }
            return [];
        },
        brandLabel(creative) {
            if (!creative || !creative.brand) return '';
            if (typeof creative.brand === 'object') return creative.brand.name || '';
            return creative.brand;
        },
        formatDate(dateString) {
            return formatInRtbTZ(dateString);
        },
        async refreshPublicationFromSsp(creative, target) {
            const pub = target?.publication;
            if (!pub?.id) return;

            const processKey = `${creative.id}:${target.target_key}`;
            this.processingPublicationRefreshKey = processKey;

            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
                const url = `/api/creatives/${creative.id}/publications/${pub.id}/refresh-ssp-status`;
                const { data } = await axios.post(
                    url,
                    {},
                    {
                        headers: {
                            Accept: 'application/json',
                            'X-CSRF-TOKEN': csrfToken,
                        },
                    }
                );

                if (data?.publication) {
                    target.publication = {
                        ...(target.publication || {}),
                        ...data.publication,
                    };
                    const stateKey = this.getTargetStateKey(creative.id, target.target_key);
                    if (data.publication.api_id != null && data.publication.api_id !== '' && target.ssp === 'cityscreen') {
                        this.targetApiIds[stateKey] = data.publication.api_id;
                    }
                }

                this.$notify({
                    title: 'Успешно',
                    message: data?.message || 'Статус обновлён',
                    type: 'success',
                });
                this.applyCreativeAggregateFromResponse(creative, data);
            } catch (error) {
                const message =
                    error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    'Не удалось обновить статус из SSP';
                this.$notify({
                    title: 'Ошибка',
                    message,
                    type: 'error',
                });
            } finally {
                this.processingPublicationRefreshKey = null;
            }
        },
    },
};
</script>

<style scoped>
.moderation-card--rtb {
    display: block;
    padding: 0;
    overflow: visible;
}

.moderation-card__summary {
    width: 100%;
    display: grid;
    grid-template-columns: 28px 1fr auto;
    gap: 12px;
    align-items: center;
    padding: 12px 14px;
}

.moderation-card__expand,
.moderation-card__status-hit {
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;
}

.moderation-card__expand {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
}

.moderation-card__expand:hover,
.moderation-card__summary:hover .moderation-card__expand {
    background: #f1f5f9;
}

.moderation-card__summary-main {
    min-width: 0;
    display: grid;
    gap: 6px;
}

.moderation-card__title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.moderation-card__title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.moderation-card__meta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    font-size: 12px;
    color: #64748b;
}

.moderation-card__info-wrap {
    position: relative;
    flex-shrink: 0;
}

.moderation-card__info-btn {
    border: 0;
    background: transparent;
    padding: 0;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
}

.moderation-card__info-btn:hover,
.moderation-card__info-btn[aria-expanded='true'] {
    color: #334155;
}

.moderation-card__info-popover {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 20;
    min-width: 260px;
    max-width: 360px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

.moderation-card__info-row {
    display: grid;
    grid-template-columns: 84px 1fr;
    gap: 8px;
    font-size: 12px;
    line-height: 1.4;
}

.moderation-card__info-row + .moderation-card__info-row {
    margin-top: 8px;
}

.moderation-card__info-label {
    color: #94a3b8;
    font-weight: 600;
}

.moderation-card__info-value {
    color: #334155;
    word-break: break-word;
}

.moderation-card__chevron {
    width: 8px;
    height: 8px;
    border-right: 2px solid #64748b;
    border-bottom: 2px solid #64748b;
    transform: rotate(-45deg);
    transition: transform 0.15s ease;
    margin-top: 2px;
}

.moderation-card__chevron--open {
    transform: rotate(45deg);
    margin-top: -2px;
}

.moderation-card__summary-status {
    flex-shrink: 0;
}

.moderation-card__targets-chip {
    color: #475569;
}

.chip--required {
    background: #eff6ff;
    color: #1d4ed8;
    border-color: #bfdbfe;
}

.chip--coverage {
    background: #ecfdf5;
    color: #047857;
    border-color: #a7f3d0;
}

.moderation-card__details {
    padding: 0 14px 14px;
    border-top: 1px solid #eef2f7;
}

.moderation-card--grid {
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 20px;
    padding-top: 14px;
}

.targets-table-wrap {
    margin-top: 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
}

.targets-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.targets-table th,
.targets-table td {
    border-bottom: 1px solid #eef2f7;
    padding: 10px;
    text-align: left;
    vertical-align: middle;
}

.targets-table thead th {
    background: #f8fafc;
    color: #374151;
    font-weight: 600;
}

.targets-table__row--required td {
    background: #f8fbff;
}

.targets-table__row--optional td {
    color: #4b5563;
}

.targets-table__row--optional .target-label__name {
    font-weight: 400;
    color: #4b5563;
}

.targets-table__platform-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;
}

.targets-table__platform-chevron {
    flex-shrink: 0;
}

.targets-table__orgs-summary {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    color: #475569;
    font-size: 13px;
}

.targets-table__org-row td:first-child {
    background: #f8fafc;
}

.targets-table__ssp-indent {
    width: 28px;
    padding-left: 10px;
    padding-right: 0;
}

.target-api-input {
    width: 4.5rem;
    max-width: 5rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 6px 8px;
    font-variant-numeric: tabular-nums;
}

.target-label {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.target-label__name {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-weight: 500;
    color: #111827;
}

.target-required-badge {
    display: inline-block;
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.4;
    border-radius: 999px;
    background: #dbeafe;
    color: #1d4ed8;
    white-space: nowrap;
}

.target-label__oid {
    font-size: 12px;
    color: #6b7280;
}

.target-label__ssp-links {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
}

.target-label__ssp-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    text-decoration: none;
    line-height: 0;
}

.target-label__ssp-icon:hover {
    color: #4b5563;
}

.target-label__bindings {
    font-size: 12px;
    color: #2563eb;
}

.status-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    max-width: 280px;
}

.status-cell__row {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.publication-comment {
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.35;
    color: #6b7280;
    max-width: 100%;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.status-pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #cbd5e1;
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 12px;
    line-height: 1.2;
}

.status-pill--ok {
    border-color: #16a34a;
    color: #166534;
}

.status-pill--fail {
    border-color: #dc2626;
    color: #991b1b;
}

.status-pill--pending {
    border-color: #2563eb;
    color: #1d4ed8;
}

.status-pill--idle {
    border-color: #6b7280;
    color: #4b5563;
}

.status-pill--archived {
    border-color: #9ca3af;
    color: #4b5563;
    background: #f9fafb;
}

.target-error {
    color: #dc2626;
    margin-top: 8px;
    font-size: 13px;
}

.moderation-card__previews {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.moderation-card__preview {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
}

.preview-locale {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 1;
    background: rgba(15, 23, 42, 0.75);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
}

.preview-thumb {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: block;
}

.targets-table__rowspan {
    vertical-align: top;
}

.locale-block--empty {
    color: #94a3b8;
}

.citix-frames {
    position: relative;
    margin-top: 8px;
}

.citix-frames__trigger,
.citix-frames__actions .act {
    font-size: 12px;
}

.citix-frames__trigger {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #fff;
    color: #0f172a;
    padding: 4px 8px;
    cursor: pointer;
}

.citix-frames__panel {
    position: absolute;
    z-index: 20;
    top: calc(100% + 4px);
    left: 0;
    width: min(360px, 70vw);
    max-height: 240px;
    overflow: auto;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
    padding: 6px;
}

.citix-frames__option {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    padding: 4px 6px;
    cursor: pointer;
}

.citix-frames__option-label {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.citix-frames__gid {
    font-size: 12px;
    font-weight: 600;
    color: #0f172a;
}

.citix-frames__address {
    font-size: 11px;
    color: #64748b;
}

.citix-frames__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
}

.citix-frames__hint {
    font-size: 11px;
    color: #64748b;
}

.locale-pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #cbd5e1;
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 600;
    color: #334155;
}

@media (max-width: 900px) {
    .moderation-card--grid {
        grid-template-columns: 1fr;
    }
}
</style>
